import { catchError, Subscription, throwError } from 'rxjs';
import { ChatGptAction, ChatGptIntentChip } from 'src/app/core/types/chatgpt/types-chatgpt';
import { ChatGptGlobalSearchResult, SureSimSearchResultRow } from 'src/app/core/interfaces/chatgpt/interfaces-chatgpt';
import { ChatGptService } from 'src/app/core/services/utelize/general/chatgpt-service';
import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ConfigSystemParams } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { SureSimSearchService } from 'src/app/core/services/utelize/general/suresim-search-service';

interface TopbarSearchGroup {
  kind : ChatGptIntentChip;
  label: string;
  rows : SureSimSearchResultRow[];
}

interface TopbarSearchKindChip {
  count: number;
  kind : ChatGptIntentChip;
  label: string;
}

@Component({
  selector: 'app-topbar-search',
  templateUrl: './topbar-search.component.html',
  styleUrls: ['./topbar-search.component.scss']
})
export class TopbarSearchComponent implements OnDestroy, OnInit {

  @Output() modalActionRequested: EventEmitter<ChatGptAction>=new EventEmitter<ChatGptAction>();

  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;

  get showTooltips() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.ShowTooltips, false); }

  private static readonly groupOrder: Array<{ kind: ChatGptIntentChip; label: string }>=[
    { kind: 'company', label: 'Companies' },
    { kind: 'user',    label: 'Users'     },
    { kind: 'sim',     label: 'SIMs'      },
    { kind: 'ticket',  label: 'Tickets'   },
  ];

  private static readonly kindPrefixes: Array<{ kind: ChatGptIntentChip; pattern: RegExp }>=[
    { kind: 'company', pattern: /^(?:company|companies)\s+(.+)$/i                               },
    { kind: 'user',    pattern: /^(?:portal\s+user|sim\s+user|esim\s+user|user|users)\s+(.+)$/i },
    { kind: 'sim',     pattern: /^(?:sim|sims|esim|iccid|device)\s+(.+)$/i                      },
    { kind: 'ticket',  pattern: /^(?:ticket|tickets)\s+(.+)$/i                                  },
  ];

  private static readonly minimumSearchLength: number=2;

  private rows     : SureSimSearchResultRow[]=[];
  private searchSub: Subscription | undefined;

  public activeKey       : string="";
  public expanded        : boolean=false;
  public hint            : string="";
  public isSearching     : boolean=false;
  public kindChips       : TopbarSearchKindChip[]=[];
  public lastSearchedText: string="";
  public lastSearchedTerm: string="";
  public searchText      : string="";
  public selectedKind    : ChatGptIntentChip='all';
  public showResults     : boolean=false;
  public totalRows       : number=0;
  public truncated       : boolean=false;
  public visibleGroups   : TopbarSearchGroup[]=[];

  constructor(
    private chatGptService      : ChatGptService,
    private configSystemService : ConfigSystemService,
    private elementRef          : ElementRef<HTMLElement>,
    private exceptionsService   : ExceptionsService,
    private sureSimSearchService: SureSimSearchService
  ) { }

  public ngOnInit(): void {
    try {

    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.searchSub?.unsubscribe();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Private

  private buildRows(result: ChatGptGlobalSearchResult): SureSimSearchResultRow[] {
    try {
      const grouped=new Map<string, SureSimSearchResultRow>();

      (result?.actions ?? [])
        .filter(action => this.canShowAction(action))
        .forEach(action => {
          const key     =this.sureSimSearchService.actionCardKey(action);
          const existing=grouped.get(key);

          if (existing) {
            existing.actions.push(action);
            return;
          }

          const kind=this.sureSimSearchService.actionCardKind(action);

          grouped.set(key, {
            actions: [action],
            key    : key,
            kind   : kind,
            meta   : this.rowMeta(action, kind),
            title  : this.rowTitle(action, kind)
          });
        });

      const rows=Array.from(grouped.values());

      rows.forEach(row => {
        row.actions.sort((a, b) => this.sureSimSearchService.actionDisplayOrder(a) - this.sureSimSearchService.actionDisplayOrder(b));
      });

      return rows;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  private canShowAction(action: ChatGptAction): boolean {
    try {
      if (action.type !== 'open_modal')
        return true;

      return this.chatGptService.canExecuteModalAction(action.modal);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private closeResults(collapse: boolean): void {
    try {
      this.showResults=false;

      if (collapse)
        this.expanded=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private flatVisibleRows(): SureSimSearchResultRow[] {
    try {
      return this.visibleGroups.flatMap(group => group.rows);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  private focusInput(): void {
    try {
      setTimeout(() => this.searchInput?.nativeElement?.focus());
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private moveActive(step: number): void {
    try {
      const rows=this.flatVisibleRows();

      if (rows.length===0)
        return;

      const index    =rows.findIndex(row => row.key===this.activeKey);
      const nextIndex=index < 0 ? (step > 0 ? 0 : rows.length - 1) : (index + step + rows.length) % rows.length;

      this.activeKey=rows[nextIndex].key;
      this.scrollActiveIntoView();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private parseSearchText(text: string): { kind: ChatGptIntentChip; term: string } {
    try {
      let term=(text ?? '').trim().replace(/^(?:search\s+for|search|look\s+for|find)\s+/i, '').trim();

      for (const prefix of TopbarSearchComponent.kindPrefixes) {
        const match=term.match(prefix.pattern);

        if (match?.[1]?.trim())
          return { kind: prefix.kind, term: match[1].trim() };
      }

      term=term.replace(/^["']+|["']+$/g, '').trim();
      return { kind: 'all', term: term };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { kind: 'all', term: (text ?? '').trim() };
    }
  }

  private refreshVisible(): void {
    try {
      this.kindChips=TopbarSearchComponent.groupOrder
                     .map(g => ({ count: this.rows.filter(r => r.kind===g.kind).length, kind: g.kind, label: g.label }))
                     .filter(chip => chip.count > 0);

      if (this.selectedKind !== 'all' && !this.kindChips.some(chip => chip.kind===this.selectedKind))
        this.selectedKind='all';

      this.visibleGroups=TopbarSearchComponent.groupOrder
                         .filter(g => this.selectedKind==='all' || g.kind===this.selectedKind)
                         .map(g => ({ kind: g.kind, label: g.label, rows: this.rows.filter(r => r.kind===g.kind) }))
                         .filter(g => g.rows.length > 0);

      this.activeKey=this.flatVisibleRows()[0]?.key ?? "";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private rowMeta(action: ChatGptAction, kind: ChatGptIntentChip): string {
    try {
      const payload          =action.payload;
      const parts  : string[]=[];

      switch (kind) {
        case 'company':
          if (payload?.companyId)
            parts.push(`#${payload.companyId}`);
          break;

        case 'user':
          if (payload?.userEmail)
            parts.push(payload.userEmail);

          if (payload?.companyName)
            parts.push(payload.companyName);
          break;

        case 'sim':
          if (payload?.userName && payload?.iccid)
            parts.push(payload.iccid);

          if (payload?.userName && payload?.companyName)
            parts.push(payload.companyName);

          if (payload?.entityStatus)
            parts.push(payload.entityStatus);
          break;

        case 'ticket':
          if (payload?.entityStatus)
            parts.push(payload.entityStatus);

          if (payload?.ticket?.company)
            parts.push(payload.ticket.company);

          if (payload?.ticket?.requester)
            parts.push(payload.ticket.requester);
          break;
      }

      return parts.join(' · ');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private rowTitle(action: ChatGptAction, kind: ChatGptIntentChip): string {
    try {
      const payload=action.payload;

      switch (kind) {
        case 'company':
          return payload?.companyName ?? this.sureSimSearchService.actionLabel(action);

        case 'user':
          return payload?.userName ?? this.sureSimSearchService.actionLabel(action);

        case 'sim':
          return payload?.userName || payload?.iccid || this.sureSimSearchService.actionLabel(action);

        case 'ticket':
          return payload?.ticketTitle ? `#${payload.ticketId} ${payload.ticketTitle}` : `#${payload?.ticketId ?? ''}`;

        default:
          return this.sureSimSearchService.actionLabel(action);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private runSearch(): void {
    try {
      const text          =(this.searchText ?? '').trim();
      const { kind, term }=this.parseSearchText(text);

      this.searchSub?.unsubscribe();
      this.hint       ="";
      this.showResults=true;

      if (term.length<TopbarSearchComponent.minimumSearchLength) {
        this.hint            =`Type at least ${TopbarSearchComponent.minimumSearchLength} characters to search.`;
        this.isSearching     =false;
        this.lastSearchedText="";
        return;
      }

      this.isSearching     =true;
      this.lastSearchedText=text;
      this.lastSearchedTerm=term;
      this.selectedKind    =kind;

      this.searchSub=this.sureSimSearchService.globalSearch(term)
        .pipe(
          catchError((error) => {
            return throwError(() => new Error(this.exceptionsService.formatHttpError(error)));
          })
        )
        .subscribe({
          next : result => {
            this.isSearching=false;
            this.rows       =this.buildRows(result);
            this.totalRows  =this.rows.length;
            this.truncated  =result?.truncated ?? false;
            this.refreshVisible();
          },
          error: error => {
            this.isSearching=false;
            this.rows       =[];
            this.totalRows  =0;
            this.truncated  =false;
            this.refreshVisible();
            this.exceptionsService.handleException(new Error(error));
          }
        });
    }
    catch(e) {
      this.isSearching=false;
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private scrollActiveIntoView(): void {
    try {
      setTimeout(() => {
        const el=this.elementRef.nativeElement.querySelector('.ss-search-row.active');
        el?.scrollIntoView({ block: 'nearest' });
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Public

  public actionShortLabel(action: ChatGptAction): string {
    try {
      if (action.type==='navigate') {
        switch ((action.destination ?? '').toLowerCase()) {
          case 'companies':
            return 'Companies';
          case 'managesims':
            return 'SIM';
          case 'portalusers':
            return 'User - Portal';
          case 'simusers':
            return 'User - SIM';
          case 'ticketshub':
            return 'Ticket';
          default:
            return this.sureSimSearchService.actionLabel(action);
        }
      }

      switch (action.modal) {
        case 'company':
          return 'Company';
        case 'manage_sim':
          return 'SIM';
        case 'ticket':
          return 'Ticket';
        case 'user_portal':
          return 'User - Portal';
        case 'user_sim':
          return 'User - SIM';
        default:
          return this.sureSimSearchService.actionLabel(action);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 'Open';
    }
  }

  public actionIcon(action: ChatGptAction): string {
    try {
      return action.type==='navigate' ? 'bi bi-table' : 'bi bi-window';
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public actionTitle(action: ChatGptAction): string {
    try {
      return this.sureSimSearchService.actionLabel(action);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public clearSearch(): void {
    try {
      this.searchSub?.unsubscribe();
      this.hint            ="";
      this.isSearching     =false;
      this.lastSearchedText="";
      this.lastSearchedTerm="";
      this.rows            =[];
      this.searchText      ="";
      this.showResults     =false;
      this.totalRows       =0;
      this.truncated       =false;
      this.refreshVisible();
      this.focusInput();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public executeAction(action: ChatGptAction | undefined, event?: Event): void {
    try {
      event?.stopPropagation();

      if (!action)
        return;

      if (action.type==='navigate') {
        if (!this.sureSimSearchService.navigate(action.destination, action.payload))
          throw new Error(`TopbarSearchComponent.executeAction() - Unknown navigation target: ${action.destination}`);

      } else {
        this.modalActionRequested.emit(action);
      }

      this.closeResults(true);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public kindIcon(kind: ChatGptIntentChip): string {
    try {
      switch (kind) {
        case 'company':
          return 'bi bi-building';
        case 'user':
          return 'bi bi-person';
        case 'sim':
          return 'bi bi-sim';
        case 'ticket':
          return 'bi bi-ticket-perforated';
        default:
          return 'bi bi-search';
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  public kindColour(kind: ChatGptIntentChip): string {
    try {
      switch (kind) {
        case 'company':
          return 'bg-primary-subtle text-primary';
        case 'user':
          return 'bg-success-subtle text-success';
        case 'sim':
          return 'bg-info-subtle text-info';
        case 'ticket':
          return 'bg-warning-subtle text-warning';
        default:
          return 'bg-light text-body';
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return '';
    }
  }

  @HostListener('document:mousedown', ['$event'])
  public onDocumentMouseDown(event: MouseEvent): void {
    try {
      if (!this.expanded && !this.showResults)
        return;

      if (this.elementRef.nativeElement.contains(<Node>event.target))
        return;

      this.closeResults(!(this.searchText ?? '').trim());
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onInputFocus(): void {
    try {
      if (this.lastSearchedText && this.lastSearchedText===(this.searchText ?? '').trim())
        this.showResults=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onInputKeyDown(event: KeyboardEvent): void {
    try {
      switch (event.key) {
        case 'Enter': {
          event.preventDefault();

          const text      =(this.searchText ?? '').trim();
          const sameAsLast=!!text && text===this.lastSearchedText && !this.isSearching;
          const activeRow =this.flatVisibleRows().find(row => row.key===this.activeKey);

          if (sameAsLast && this.showResults && activeRow)
            this.executeAction(activeRow.actions[0]);
          else
            this.runSearch();
          break;
        }

        case 'ArrowDown':
          if (this.showResults) {
            event.preventDefault();
            this.moveActive(1);
          }
          break;

        case 'ArrowUp':
          if (this.showResults) {
            event.preventDefault();
            this.moveActive(-1);
          }
          break;

        case 'Escape':
          event.preventDefault();

          if (this.showResults)
            this.closeResults(false);
          else {
            this.closeResults(true);
            this.searchInput?.nativeElement?.blur();
          }
          break;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onSearchClick(): void {
    try {
      this.runSearch();
      this.focusInput();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public selectKind(kind: ChatGptIntentChip): void {
    try {
      this.selectedKind=kind;
      this.refreshVisible();
      this.focusInput();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public setActive(row: SureSimSearchResultRow): void {
    try {
      this.activeKey=row.key;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleExpanded(): void {
    try {
      if (this.expanded && !(this.searchText ?? '').trim()) {
        this.closeResults(true);
        return;
      }

      if (this.expanded) {
        this.runSearch();
        return;
      }

      this.expanded=true;
      this.focusInput();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
